import React, { useState, useContext } from "react";
import { Menu, MenuItem } from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import api from "../../services/api";
import cleanTextMessage from "../../utils/cleanTextMessage";
import ConfirmationModal from "../ConfirmationModal";
import textToSpeech from "../../utils/textToSpeech";
import toastError from "../../errors/toastError";

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";

const MessageOptionsMenu = ({
  message, ticketType, menuOpen, handleClose, anchorEl, onUpdate,
  updateIsForwarding, handleOpenMessageEditModal, handleOpenMessagePropertiesModal,
  ticketStatus
}) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const { user: { profile: userProfile, id: userId } } = useContext(AuthContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [confirmationOpen, setConfirmationOpen] = useState(false);



  //  **********************
  //  ** Allow Conditions **
  //  **********************

  // ***---- User Conditions ----***
  const userIdOrAdmin = message.userId === userId || userProfile === "admin";
  const notTicketPendingAndUserNotAdmin = !(ticketStatus === "pending" && userProfile === "user");

  // ***---- Actions Conditions ----***
  const allowDelete = userIdOrAdmin
    && (
      (
        message.fromMe
        && !message.isDeleted
        && [0, 1].includes(ticketType)
        && notTicketPendingAndUserNotAdmin
        && !message.isInternal
        && ![
          DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
        ].includes(message.mediaType)
      )
      || ([3, 4].includes(ticketType) && message.userId === userId)
    );

  const allowEdit = userIdOrAdmin
    && (
      (
        ticketType === 0
        && message.fromMe
        && ![0, 1].includes(message.ack)
        && notTicketPendingAndUserNotAdmin
        && !message.isViewOnce
        && (
          (
            [
              DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.chat
              , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
            ].includes(message.mediaType)
          )
          || (
            message.body
            && !message.isInternal
            && [
              DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
              , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
              , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
            ].includes(message.mediaType)
          )
        )
      )

      || (
        ticketType === 1
        && [
          DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.chat
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
        ].includes(message.mediaType)
      )
      
      || (
        [3, 4].includes(ticketType)
        && message.userId === userId
        && [
          DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.chat
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
          , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
        ].includes(message.mediaType)
      )
    );

  const allowReplyAndForward = (
      (ticketType === 0 && message.ticket)
      || [1, 3, 4].includes(ticketType)
    )
    && (!message.fromMe || (message.fromMe && ![0, 9].includes(message.ack)))
    && notTicketPendingAndUserNotAdmin
    && !message.isInternal
    && ![
      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonResponse
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listResponse
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce
    ].includes(message.mediaType);

  const allowForward = [0, 3, 4].includes(ticketType)
    && !message.isViewOnce
    && ![
      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.group_invite
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.template
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.templateButtonReply
    ].includes(message.mediaType);

  const allowCopy = message.body !== ""
    && [
      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.chat
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.metaAiInvoke
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
    ].includes(message.mediaType);

  const allowStarAndUnstar = message.ticket !== undefined
    && ticketType === 0
    && notTicketPendingAndUserNotAdmin
    && !message.isViewOnce
    && !message.isInternal
    && ![
      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonResponse
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.call
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listResponse
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce
    ].includes(message.mediaType);

  const allowSpeech = message.ticket !== undefined
    && [
      DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.buttonResponse
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.chat
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listCreation
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.listResponse
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.metaAiInvoke
      , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation
    ].includes(message.mediaType);

  const allowProperties = message.ticket !== undefined && message.createdAt !== undefined;



  //  ***************
  //  ** Functions **
  //  ***************
  const handleDeleteMessage = async () => {
    try {
      await api.delete(`/messages/${message.id}`);
    }
    catch (exception) {
      console.log("Handle Delete Message Exception:", exception);
      toastError(exception);
    }
  };

  const handleReplyMessage = () => {
    setReplyingMessage(message);
    handleClose();
  };

  const handleEditMessage = async () => {
    handleClose();

    try {
      await api.put(`/messagesValidateEdit/${message.id}`, { ticketType });
      handleOpenMessageEditModal();
    }
    catch(exception) {
      console.log("Handle Edit Message Exception:", exception);
      toastError(exception);
    }
  };

  const handleForwardMessage = () => {
    onUpdate(true);
    updateIsForwarding(true);
    handleClose();
  }

  const handleStarMessage = async () => {
    try {
      handleClose();
      await api.put(`/messages/${message.id}`);
    }
    catch (exception) {
      console.log("Handle Star Message Exception:", exception);
      toastError(exception);
    }
  };

  const handleSpeechMessage = () => {
    handleClose();
    textToSpeech(message.body);
  };

  const handlePropertiesMessage = () => {
    handleClose();
    handleOpenMessagePropertiesModal(message);
  };

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
    handleClose();
  };

  const getTextSelection = () => {
    if (!window.getSelection) return "";
    return window.getSelection().toString();
  };

  const getTextMessageBodyWithoutSignature = (message) => {
    const bodySplit = message.body.split("\n");
    const signatureRegex = /^\*[^*]*\*$/;
    if (signatureRegex.test(bodySplit[0])) bodySplit.shift();
    return bodySplit.join("\n");
  };

  const chooseTextToCopy = (message) => {
    return getTextSelection() === ""
      ? cleanTextMessage(getTextMessageBodyWithoutSignature(message))
      : cleanTextMessage(getTextSelection());
  };

  const handleCopyMessage = async () => {
    handleClose();
    
    const textToCopy = chooseTextToCopy(message);

    await navigator.clipboard.writeText(textToCopy);
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <>
      {/* ***---- Confirmation Modal for Deleting Messages ----*** */}
      <ConfirmationModal
        title={i18n.t("messageOptionsMenu.confirmationModal.title")}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteMessage}
      >{i18n.t("messageOptionsMenu.confirmationModal.message")}</ConfirmationModal>

      <Menu
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        open={menuOpen}
        onClose={handleClose}
        onContextMenu={event => event.preventDefault()}
      >
        {/* ***---- Action: Delete ----*** */}
        {allowDelete && (
          <MenuItem onClick={handleOpenConfirmationModal}>{i18n.t("messageOptionsMenu.delete")}</MenuItem>
        )}

        {/* ***---- Action: Edit ----*** */}
        {allowEdit && (
          <MenuItem onClick={async () => await handleEditMessage()}>{i18n.t("messageOptionsMenu.edit")}</MenuItem>
        )}

        {/* ***---- Action: Reply and Forward ----*** */}
        {allowReplyAndForward && (
          <>
            <MenuItem onClick={handleReplyMessage}>{i18n.t("messageOptionsMenu.reply")}</MenuItem>

            {allowForward && (
              <MenuItem onClick={handleForwardMessage}>{i18n.t("messageOptionsMenu.forward")}</MenuItem>
            )}
          </>
        )}

        {/* ***---- Action: Copy ----*** */}
        {allowCopy && (
          <MenuItem onClick={handleCopyMessage}>{i18n.t("messageOptionsMenu.copy")}</MenuItem>
        )}

        {/* ***---- Action: Star ----*** */}
        {allowStarAndUnstar && !message.isStarred && (
          <MenuItem onClick={async () => await handleStarMessage()}>{i18n.t("messageOptionsMenu.star")}</MenuItem>
        )}

        {/* ***---- Action: Unstar ----*** */}
        {allowStarAndUnstar && message.isStarred && (
          <MenuItem onClick={async () => await handleStarMessage()}>{i18n.t("messageOptionsMenu.unstar")}</MenuItem>
        )}

        {/* ***---- Action: Speech ----*** */}
        {window.speechSynthesis && allowSpeech && (
          <MenuItem onClick={handleSpeechMessage}>{i18n.t("messageOptionsMenu.speech")} {window.speechSynthesis?.speaking ? "❌" : "✔️"}</MenuItem>
        )}

        {/* ***---- Action: Properties ----*** */}
        {allowProperties && (
          <MenuItem onClick={handlePropertiesMessage}>{i18n.t("messageOptionsMenu.properties")}</MenuItem>
        )}

        {/* ***---- Action: Close ----*** */}
        <MenuItem onClick={handleClose}>{i18n.t("messageOptionsMenu.close")}</MenuItem>
      </Menu>
    </>
  );
};

export default MessageOptionsMenu;
